import {
    Box, Button, Flex, HStack, InfoBox, Modal, Select, Text,
    Textarea, VStack
} from 'components/ui';
import { useTranslation } from 'hooks/useTranslation';
import { useEffect, useState } from 'react';
import { MdClose } from 'react-icons/md';
import { testMasking, TestMaskingDto } from 'services/maskingService';
import { serverService } from 'services/serverService';
import { ToolConfig, toolService } from 'services/toolService';

interface TestMaskingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface Server {
  id: string;
  name: string;
}

const TestMaskingModal = ({ isOpen, onClose }: TestMaskingModalProps) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [servers, setServers] = useState<Server[]>([]);
  const [tools, setTools] = useState<ToolConfig[]>([]);
  const [loadingTools, setLoadingTools] = useState(false);
  const [formData, setFormData] = useState<TestMaskingDto>({
    jsonData: '',
    serverId: undefined,
    toolName: undefined,
  });
  const [result, setResult] = useState<{
    originalJson: string;
    maskedJson: string;
    appliedRules: string[];
  } | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadServers();
    }
  }, [isOpen]);

  const loadServers = async () => {
    try {
      const data = await serverService.getAllServers();
      setServers(data.map(s => ({ id: s.id!, name: s.name })));
    } catch (error) {
      console.error('Error loading servers:', error);
    }
  };

  const loadTools = async (serverId: string) => {
    if (!serverId) {
      setTools([]);
      return;
    }
    setLoadingTools(true);
    try {
      const data = await toolService.getServerTools(serverId);
      setTools(data);
    } catch (error) {
      console.error('Error loading tools:', error);
      setTools([]);
    } finally {
      setLoadingTools(false);
    }
  };

  useEffect(() => {
    if (formData.serverId) {
      loadTools(formData.serverId);
    } else {
      setTools([]);
      setFormData(prev => ({ ...prev, toolName: undefined }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.serverId]);

  const handleTest = async () => {
    if (!formData.jsonData) {
      setError(t('app.security.testMaskingModal.jsonDataRequired'));
      return;
    }

    // Validate JSON
    try {
      JSON.parse(formData.jsonData);
    } catch (e) {
      setError(t('app.security.testMaskingModal.jsonInvalid'));
      return;
    }

    setLoading(true);
    setError('');
    try {
      const data = await testMasking(formData);
      setResult(data);
    } catch (err: any) {
      setError(err.message || t('app.security.testMaskingModal.failed'));
    } finally {
      setLoading(false);
    }
  };
  const handleClose = () => {
    setResult(null);
    setFormData({
      jsonData: '',
      serverId: undefined,
      toolName: undefined,
    });
    setError('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl">
      <Modal.Header>
        <Flex justify="space-between" align="center">
          <h3 style={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: 'var(--modal-header-text)'
          }}>
            {t('app.security.testMaskingModal.title')}
          </h3>
          <Modal.CloseTrigger asChild>
            <button style={{
              color: 'var(--modal-close-icon)',
              transition: 'color 0.2s'
            }}
              onMouseEnter={(e) => e.currentTarget.style.color = 'var(--modal-close-icon-hover)'}
              onMouseLeave={(e) => e.currentTarget.style.color = 'var(--modal-close-icon)'}>
              <MdClose style={{ height: '1.5rem', width: '1.5rem' }} />
            </button>
          </Modal.CloseTrigger>
        </Flex>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <InfoBox variant="error" mb={4}>
            <HStack justify="space-between">
              <Text color="red.700">{error}</Text>
              <Button onClick={() => setError('')} size="sm" variant="ghost">
                {t('app.security.testMaskingModal.dismiss')}
              </Button>
            </HStack>
          </InfoBox>
        )}

        <VStack gap={4} align="stretch">
          <Textarea
            label={t('app.security.testMaskingModal.jsonDataLabel')}
            value={formData.jsonData}
            onChange={(e) => setFormData({ ...formData, jsonData: e.target.value })}
            placeholder={t('app.security.testMaskingModal.jsonDataPlaceholder')}
            rows={8}
            fontFamily="monospace"
            fontSize="sm"
            required
          />

          <Select
            label={t('app.security.testMaskingModal.serverLabel')}
            value={formData.serverId ? [formData.serverId] : []}
            onValueChange={(details: any) =>
              setFormData({ ...formData, serverId: details.value[0] || undefined })
            }
            items={[
              { label: t('app.security.testMaskingModal.serverPlaceholder'), value: '' },
              ...servers.map(s => ({ label: s.name, value: s.id })),
            ]}
          />

          <Select
            label={t('app.security.testMaskingModal.toolLabel')}
            value={formData.toolName ? [formData.toolName] : []}
            onValueChange={(details: any) =>
              setFormData({ ...formData, toolName: details.value[0] || undefined })
            }
            items={[
              { label: t('app.security.testMaskingModal.toolPlaceholder'), value: '' },
              ...tools.map((tool) => ({
                label: tool.name,
                value: tool.name,
              })),
            ]}
            disabled={!formData.serverId || loadingTools}
          />

          {result && (
            <>
              <Box style={{ height: '1px', backgroundColor: '#e5e7eb', margin: '1rem 0' }} />
              <Box>
                <Text fontWeight="bold" mb={2}>{t('app.security.testMaskingModal.result')}</Text>

                {result.appliedRules.length > 0 && (
                  <Box mb={4}>
                    <Text fontSize="sm" color="gray.600" mb={1}>
                      {t('app.security.testMaskingModal.appliedRules', { count: result.appliedRules.length })}
                    </Text>
                    <VStack align="stretch" gap={1}>
                      {result.appliedRules.map((rule, idx) => (
                        <Text key={idx} fontSize="xs" color="blue.600" fontFamily="monospace">
                          • {rule}
                        </Text>
                      ))}
                    </VStack>
                  </Box>
                )}

                <VStack align="stretch" gap={3}>
                  <Box>
                    <Text fontSize="sm" color="gray.600" mb={1}>
                      {t('app.security.testMaskingModal.originalJson')}
                    </Text>
                    <Box
                      as="pre"
                      p={3}
                      borderRadius="md"
                      bg="blue.50"
                      color="blue.900"
                      fontFamily="monospace"
                      fontSize="sm"
                      overflow="auto"
                      maxH="200px"
                    >
                      {JSON.stringify(JSON.parse(result.originalJson), null, 2)}
                    </Box>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color="gray.600" mb={1}>
                      {t('app.security.testMaskingModal.maskedJson')}
                    </Text>
                    <Box
                      as="pre"
                      p={3}
                      borderRadius="md"
                      bg="green.50"
                      color="green.900"
                      fontFamily="monospace"
                      fontSize="sm"
                      overflow="auto"
                      maxH="200px"
                    >
                      {JSON.stringify(JSON.parse(result.maskedJson), null, 2)}
                    </Box>
                  </Box>
                </VStack>
              </Box>
            </>
          )}
        </VStack>
      </Modal.Body>
      <Modal.Footer>
        <HStack justify="flex-end">
          <Button onClick={handleClose} variant="ghost">
            {t('app.security.testMaskingModal.close')}
          </Button>
          <Button
            onClick={handleTest}
            colorPalette="blue"
            loading={loading}
          >
            {t('app.security.testMaskingModal.test')}
          </Button>
        </HStack>
      </Modal.Footer>
    </Modal>
  );
};

export default TestMaskingModal;
